import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';
import { IconButton, Tooltip } from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import moment from 'moment';
import { toast } from 'react-toastify';
import SubHeader from '../../components/Subheader/Subheader';
import CustomBreadcrumbs from '../../components/Tools/Breadcumbs';
import Spinner from '../../components/Tools/spinner';
import { getUtilizationCertificateRecords } from '../../helper/Api';
import { openUcDocumentOrToast } from '../../helper/secureDocuments';
import { MONTH_NAMES } from '../../components/DashboardBox/DashboardComponents/utilizationReportUtils';

const formatUcType = (ucType) => {
    const value = String(ucType || '').trim().toLowerCase();
    if (value === 'self') return 'Self';
    if (value === 'consolidated') return 'Consolidated';
    return ucType || '-';
};

const formatMonthLabel = (month) => {
    const monthNumber = Number(month);
    if (!Number.isFinite(monthNumber) || monthNumber < 1 || monthNumber > 12) {
        return month || '-';
    }
    return MONTH_NAMES[monthNumber - 1];
};

const UtilizationCertificateList = () => {
    const { financialYears } = useSelector((state) => state.authData);
    const fyList = financialYears?.payload?.fy_list || [];
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const [fyId, setFyId] = useState(fyIdRef.current);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const fyNameById = useMemo(() => {
        const map = {};
        (fyList || []).forEach((fy) => {
            map[String(fy.id)] = fy.fy_name || fy.name || `${fy.start_year}-${fy.end_year}` || String(fy.id);
        });
        return map;
    }, [fyList]);

    const handlePageTrigger = () => {
        fyIdRef.current = localStorage.getItem('globalDateId');
        setFyId(fyIdRef.current);
        setCurrentPage(1);
    };

    const fetchRecords = async (page = currentPage, perPage = rowsPerPage) => {
        try {
            setLoading(true);
            const offset = (page - 1) * perPage;
            const response = await getUtilizationCertificateRecords({
                limit: perPage,
                offset,
                fy_id: fyIdRef.current || '',
            });

            if (response?.data?.status) {
                setData(response.data.result || []);
                setTotalRows(Number(response.data.total) || 0);
            } else {
                setData([]);
                setTotalRows(0);
                toast.error(response?.data?.message || 'Failed to fetch utilization certificates', {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (error) {
            setData([]);
            setTotalRows(0);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch utilization certificates', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecords(currentPage, rowsPerPage);
    }, [currentPage, rowsPerPage, fyId]);

    const handleDownload = async (row) => {
        if (!row?.id && !row?.s3_url && !row?.document_ref) {
            toast.error('Certificate not available', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        // List may still return legacy S3 s3_url — open via documents/serve/utilization_certificate/{id}
        await openUcDocumentOrToast(row.id, row.s3_url || row.document_ref);
    };

    const columns = [
        {
            name: '#',
            width: '70px',
            cell: (_row, index) => (currentPage - 1) * rowsPerPage + index + 1,
        },
        {
            name: 'Financial Year',
            selector: (row) => fyNameById[String(row.fy_id)] || row.fy_id || '-',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Month',
            selector: (row) => formatMonthLabel(row.month),
            sortable: true,
            minWidth: '120px',
        },
        {
            name: 'UC Type',
            selector: (row) => formatUcType(row.uc_type),
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Department',
            selector: (row) => row.department_name || '-',
            sortable: true,
            minWidth: '200px',
            cell: (row) => (
                <div style={{ lineHeight: 1.25, padding: '4px 0' }}>
                    <div style={{ fontWeight: 600 }}>{row.department_name || '-'}</div>
                    <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                        Level: {row.department_level || '-'}
                    </div>
                </div>
            ),
        },
        {
            name: 'Generated At',
            selector: (row) => row.created_at || '',
            sortable: true,
            minWidth: '180px',
            cell: (row) =>
                row?.created_at
                    ? moment(row.created_at).format('DD MMM YYYY, hh:mm A')
                    : '-',
        },
        {
            name: 'Action',
            width: '100px',
            center: true,
            cell: (row) => (
                <Tooltip title="Download Certificate" arrow>
                    <IconButton
                        size="small"
                        onClick={() => handleDownload(row)}
                        sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid #1976d2',
                            color: '#1976d2',
                            borderRadius: '6px',
                        }}
                    >
                        <AiOutlineVerticalAlignBottom size={16} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
    };

    return (
        <div>
            {loading && <Spinner />}
            <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />
            <div className="row" style={{ padding: '10px 16px' }}>
                <div className="col-md-6">
                    <h4 style={{ color: '#28326f', margin: 0 }}>Utilization Certificate</h4>
                    <CustomBreadcrumbs linksArray={[]} title="UC : Utilization Certificate" />
                </div>
            </div>
            <div className="scheme p-3">
                <DataTable
                    columns={columns}
                    data={data}
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    paginationDefaultPage={currentPage}
                    paginationRowsPerPageOptions={[25, 50, 100]}
                    onChangePage={(page) => setCurrentPage(page)}
                    onChangeRowsPerPage={(newPerPage) => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                    }}
                    noDataComponent={
                        <div style={{ padding: '24px', color: '#6b7280' }}>
                            No utilization certificates found
                        </div>
                    }
                    dense
                />
            </div>
        </div>
    );
};

export default UtilizationCertificateList;
